// Comprehensive list of all button-level permissions grouped by section
export const PERMISSION_GROUPS = [
  {
    name: 'Dashboard',
    permissions: [
      { key: 'view_dashboard', label: 'View Dashboard', description: 'Access to main dashboard with statistics' },
    ]
  },
  {
    name: 'Customers',
    permissions: [
      { key: 'view_customers', label: 'View Customers', description: 'View customer list and details' },
      { key: 'add_customer', label: 'Add Customer', description: 'Create new customers' },
      { key: 'edit_customer', label: 'Edit Customer', description: 'Modify customer information' },
      { key: 'delete_customer', label: 'Delete Customer', description: 'Remove customers from system' },
      { key: 'generate_statement', label: 'Generate Statement', description: 'Create customer statements' },
    ]
  },
  {
    name: 'Price Lists',
    permissions: [
      { key: 'view_price_lists', label: 'View Price Lists', description: 'View customer price lists' },
      { key: 'manage_price_lists', label: 'Manage Price Lists', description: 'Create and edit customer price lists' },
      { key: 'delete_price_lists', label: 'Delete Price Lists', description: 'Remove price list entries' },
    ]
  },
  {
    name: 'Orders',
    permissions: [
      { key: 'view_orders', label: 'View Orders', description: 'View order list and details' },
      { key: 'create_order', label: 'Create Order', description: 'Create new orders' },
      { key: 'edit_order', label: 'Edit Order', description: 'Modify existing orders' },
      { key: 'delete_order', label: 'Delete Order', description: 'Remove orders from system' },
      { key: 'update_order_status', label: 'Update Order Status', description: 'Change order status' },
      { key: 'generate_invoice', label: 'Generate Invoice', description: 'Create invoice PDFs' },
      { key: 'generate_delivery_note', label: 'Generate Delivery Note', description: 'Create delivery note PDFs' },
      { key: 'record_payment', label: 'Record Payment', description: 'Record customer payments' },
    ]
  },
  {
    name: 'Products',
    permissions: [
      { key: 'view_products', label: 'View Products', description: 'View product list and details' },
      { key: 'add_product', label: 'Add Product', description: 'Create new products' },
      { key: 'edit_product', label: 'Edit Product', description: 'Modify product information' },
      { key: 'delete_product', label: 'Delete Product', description: 'Remove products from system' },
      { key: 'bulk_upload_products', label: 'Bulk Upload Products', description: 'Upload products via Excel' },
      { key: 'merge_products', label: 'Merge Products', description: 'Merge duplicate products' },
    ]
  },
  {
    name: 'Purchases',
    permissions: [
      { key: 'view_purchases', label: 'View Purchases', description: 'View purchase list and details' },
      { key: 'add_purchase', label: 'Add Purchase', description: 'Record new purchases' },
      { key: 'edit_purchase', label: 'Edit Purchase', description: 'Modify purchase records' },
      { key: 'delete_purchase', label: 'Delete Purchase', description: 'Remove purchase records' },
      { key: 'record_purchase_payment', label: 'Record Purchase Payment', description: 'Record payments to suppliers' },
    ]
  },
  {
    name: 'Expenses',
    permissions: [
      { key: 'view_expenses', label: 'View Expenses', description: 'View expense list and details' },
      { key: 'add_expense', label: 'Add Expense', description: 'Record new expenses' },
      { key: 'edit_expense', label: 'Edit Expense', description: 'Modify expense records' },
      { key: 'delete_expense', label: 'Delete Expense', description: 'Remove expense records' },
      { key: 'record_expense_payment', label: 'Record Expense Payment', description: 'Record expense payments' },
    ]
  },
  {
    name: 'Finance',
    permissions: [
      { key: 'view_finance', label: 'View Finance', description: 'Access finance dashboard' },
      { key: 'manage_cash_accounts', label: 'Manage Cash Accounts', description: 'View and adjust cash accounts' },
      { key: 'add_asset', label: 'Add Asset', description: 'Record new assets' },
      { key: 'edit_asset', label: 'Edit Asset', description: 'Modify asset records' },
      { key: 'delete_asset', label: 'Delete Asset', description: 'Remove asset records' },
      { key: 'add_liability', label: 'Add Liability', description: 'Record new liabilities' },
      { key: 'edit_liability', label: 'Edit Liability', description: 'Modify liability records' },
      { key: 'delete_liability', label: 'Delete Liability', description: 'Remove liability records' },
      { key: 'create_transfer', label: 'Create Transfer', description: 'Transfer between accounts' },
      { key: 'delete_transfer', label: 'Delete Transfer', description: 'Remove transfer records' },
      { key: 'add_capital', label: 'Add Capital', description: 'Record capital entries' },
      { key: 'delete_capital', label: 'Delete Capital', description: 'Remove capital entries' },
      { key: 'recalculate_balances', label: 'Recalculate Balances', description: 'Recalculate all account balances' },
    ]
  },
  {
    name: 'Reports',
    permissions: [
      { key: 'view_reports', label: 'View Reports', description: 'Access financial reports' },
      { key: 'view_analytics', label: 'View Analytics', description: 'Access analytics dashboard' },
      { key: 'export_reports', label: 'Export Reports', description: 'Download report files' },
      { key: 'view_profit_tracking', label: 'View Profit Tracking', description: 'Access profit tracking dashboard (sensitive)' },
    ]
  },
  {
    name: 'User Management',
    permissions: [
      { key: 'view_users', label: 'View Users', description: 'View user list' },
      { key: 'add_user', label: 'Add User', description: 'Create new users' },
      { key: 'edit_user', label: 'Edit User', description: 'Modify user information' },
      { key: 'delete_user', label: 'Delete User', description: 'Remove users from system' },
      { key: 'change_user_password', label: 'Change User Password', description: 'Reset user passwords' },
      { key: 'manage_permissions', label: 'Manage Permissions', description: 'Change user permissions' },
    ]
  },
  {
    name: 'Activity Log',
    permissions: [
      { key: 'view_audit_log', label: 'View Activity Log', description: 'Access the activity/audit log' },
    ]
  },
  {
    name: 'Landing Page',
    permissions: [
      { key: 'manage_landing', label: 'Manage Landing Page', description: 'View contact messages, moderate reviews, edit landing media & social links' },
    ]
  },
];

export const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap(g => g.permissions);
